import {
  BOT_SEQUENCE_DEFAULT_SCHEDULE_SLUG,
  BOT_SEQUENCE_INACTIVE_LEASE_GATED_STATUSES,
  BotSequenceConfigEffective,
  BotSequenceType,
  FMSCapabilityKey,
  FMSObjectType,
  LocationVarInheritance,
  ScheduleGroup
} from "@swivl-mono/types";
import { Input, Select } from "@swivl-mono/ui";
import { Link } from "react-router-dom";

import { CortexInheritancePill } from "../../CortexV3/Components/CortexInheritancePill";
import { RowInheritanceControls } from "../../CortexV3/Components/RowInheritanceControls";
import { REVERT_TO_DEFAULT } from "../../CortexV3/Components/revertTarget";
import { CapabilityCoverage } from "../Hooks/useLocationFmsCapabilities";
import { TypeDraft, TypeDraftField } from "../Hooks/useSequenceConfig";
import { SequenceScopeKind } from "../sequenceScope";
import { inheritanceFor } from "./ModeSection";

interface TypeSettingsSectionProps {
  sequenceType: BotSequenceType;
  effective: BotSequenceConfigEffective;
  draft: TypeDraft;
  scopeKind: SequenceScopeKind;
  canEdit: boolean;
  /** Coverage of the SSMCloud-only inactive-status opt-in across the scope's locations. */
  optInCoverage: CapabilityCoverage;
  optInSupportedCount: number;
  locationCount: number;
  hoursHref: string;
  /** The org's schedule groups for the "Send hours" picker (decision 50). */
  scheduleGroups: ScheduleGroup[];
  onField: (field: "minGapDays", value: number) => void;
  onScheduleSlug: (slug: string) => void;
  onStatuses: (value: string[]) => void;
  onRevert: (field: TypeDraftField) => void;
  onDiscard: (field: TypeDraftField) => void;
}

const STATUS_OPTIONS = BOT_SEQUENCE_INACTIVE_LEASE_GATED_STATUSES.map((status) => ({
  value: status,
  label: status.charAt(0).toUpperCase() + status.slice(1)
}));

interface RowProps {
  title: string;
  caption: React.ReactNode;
  inheritance: LocationVarInheritance;
  overrideStaged: boolean;
  canEdit: boolean;
  scopeKind: SequenceScopeKind;
  onRevert: () => void;
  onDiscard: () => void;
  children: React.ReactNode;
}

function SettingRow({
  title,
  caption,
  inheritance,
  overrideStaged,
  canEdit,
  scopeKind,
  onRevert,
  onDiscard,
  children
}: RowProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-grey-100 px-4 py-3 last:border-b-0">
      <div className="min-w-0">
        <div className="body-sm-alt text-grey-900">{title}</div>
        <div className="body-sm text-grey-600">{caption}</div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {canEdit ? (
          <RowInheritanceControls
            inheritance={inheritance}
            overrideStaged={overrideStaged}
            showCustomize={false}
            revertLabel={scopeKind === "org" ? REVERT_TO_DEFAULT : undefined}
            onCustomize={() => undefined}
            onRevert={onRevert}
            onDiscard={onDiscard}
          />
        ) : (
          <CortexInheritancePill inheritance={inheritance} />
        )}
        {children}
      </div>
    </div>
  );
}

/** Min gap, Past Due inactive-status opt-ins (SSMCloud), and the link to the shared send hours. No channel or schedule field (decision 21). */
export function TypeSettingsSection({
  sequenceType,
  effective,
  draft,
  scopeKind,
  canEdit,
  optInCoverage,
  optInSupportedCount,
  locationCount,
  hoursHref,
  scheduleGroups,
  onField,
  onScheduleSlug,
  onStatuses,
  onRevert,
  onDiscard
}: TypeSettingsSectionProps): React.ReactElement {
  const minGap = draft.minGapDays.kind === "set" ? draft.minGapDays.value : effective.minGapDays.value;
  const statuses =
    draft.pastDueInactiveOptInStatuses.kind === "set"
      ? draft.pastDueInactiveOptInStatuses.value
      : draft.pastDueInactiveOptInStatuses.kind === "revert"
        ? []
        : (effective.pastDueInactiveOptInStatuses.value ?? []);
  const showOptIns = sequenceType === BotSequenceType.PAST_DUE && optInCoverage !== "none";
  const scheduleSlug =
    draft.scheduleSlug.kind === "set"
      ? draft.scheduleSlug.value
      : draft.scheduleSlug.kind === "revert"
        ? BOT_SEQUENCE_DEFAULT_SCHEDULE_SLUG
        : (effective.scheduleSlug.value ?? BOT_SEQUENCE_DEFAULT_SCHEDULE_SLUG);
  const scheduleOptions = scheduleGroups.map((group) => ({ value: group.slug, label: group.label }));
  if (!scheduleOptions.some((option) => option.value === scheduleSlug)) {
    scheduleOptions.push({ value: scheduleSlug, label: scheduleSlug });
  }

  const handleMinGapChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const parsed = Number.parseInt(event.target.value, 10);
    onField("minGapDays", Number.isNaN(parsed) || parsed < 0 ? 0 : parsed);
  };

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="px-4 py-3">
        <h3 className="heading-base-alt text-grey-900">Settings</h3>
      </div>
      <div className="border-t border-grey-200">
        <SettingRow
          title="Minimum days between messages"
          caption="Fewest days between two messages to the same tenant in this sequence. 0 = no minimum."
          inheritance={inheritanceFor(effective.minGapDays.source, scopeKind)}
          overrideStaged={draft.minGapDays.kind === "set"}
          canEdit={canEdit}
          scopeKind={scopeKind}
          onRevert={() => onRevert("minGapDays")}
          onDiscard={() => onDiscard("minGapDays")}
        >
          <Input type="number" min={0} step={1} value={String(minGap)} onChange={handleMinGapChange} disabled={!canEdit} containerClassName="w-20" />
        </SettingRow>

        <SettingRow
          title="Send hours"
          caption={
            <>
              Which schedule group's hours this sequence may send in. Hours, holidays and one-off closures are set on the{" "}
              <Link to={hoursHref} className="text-orange-500 hover:text-orange-600">
                Hours page
              </Link>
              .
            </>
          }
          inheritance={inheritanceFor(effective.scheduleSlug.source, scopeKind)}
          overrideStaged={draft.scheduleSlug.kind === "set"}
          canEdit={canEdit}
          scopeKind={scopeKind}
          onRevert={() => onRevert("scheduleSlug")}
          onDiscard={() => onDiscard("scheduleSlug")}
        >
          <div className="w-56">
            <Select
              options={scheduleOptions}
              selectedValues={[scheduleSlug]}
              onChange={(selected) => selected[0] && onScheduleSlug(selected[0])}
              disabled={!canEdit}
              hideClear
            />
          </div>
        </SettingRow>

        {showOptIns && (
          <SettingRow
            title="Include inactive lease statuses"
            caption={
              optInCoverage === "all"
                ? "Leases in these FMS statuses are normally excluded from past-due outreach. Selected statuses are included."
                : `Applies at the ${optInSupportedCount} of ${locationCount} locations whose FMS supports it.`
            }
            inheritance={inheritanceFor(effective.pastDueInactiveOptInStatuses.source, scopeKind)}
            overrideStaged={draft.pastDueInactiveOptInStatuses.kind === "set"}
            canEdit={canEdit}
            scopeKind={scopeKind}
            onRevert={() => onRevert("pastDueInactiveOptInStatuses")}
            onDiscard={() => onDiscard("pastDueInactiveOptInStatuses")}
          >
            <div className="w-56">
              <Select isMultiSelect options={STATUS_OPTIONS} selectedValues={statuses} onChange={onStatuses} disabled={!canEdit} placeholder="None" />
            </div>
          </SettingRow>
        )}
      </div>
    </section>
  );
}

/** Which capability decides whether the opt-in row shows. */
export const OPT_IN_CAPABILITY: [FMSCapabilityKey, FMSObjectType] = [FMSCapabilityKey.InactiveLeaseStatusOptIn, FMSObjectType.Lease];
